import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  fetchSingleCompany,
  fetchCapCompaniesService,
  deleteCompanyService
} from 'services/modules/idp/company';
import { fetchCompanyVAPTDimensions as fetchCompanyVAPTDimensionsService } from 'services/modules/vapt/data';
import { setErrors as setErrorsSlice } from 'stores/modules/idp/companyreducer';
import { setCompanyData as setCompanyDataSlice} from 'stores/modules/idp/authreducer';
import { fetchCloudTypes as fetchCloudTypesService } from 'services/modules/vapt/alert';

import { useNotification } from 'config/Notification';
import 'modules/cap/styles/cap.css'


import {
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';


const CompanyProfile = () => {
  // Notification
  const { openNotification } = useNotification();

  // Redux dispatch
  const dispatch = useDispatch();

  // React router
  const navigate = useNavigate();

  // Redux selectors
  const user = useSelector((state) => state.auth.user);
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  const companyDataContainer = useSelector((state) => state.company.singleCompany);
  const capCompaniesContainer = useSelector((state) => state.company.capCompanies);
  const cloudTypesContainer = useSelector((state) => state.vaptAlert.cloudtypes)

  // State variables
  const [companyToDelete, setCompanyToDelete] = useState({ name: '', location: '', id: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [loading,setLoading] = useState(true);
  const companyId = selectedCompanyContainer?.companyid;
  const role = user.roles[0].label;

  // Handles navigation to historical assessments if CAP service is enabled
  const viewAssessments = () => {
    let obj = {};
    obj.companyid = companyId;
    obj.companyname = companyDataContainer && companyDataContainer.name;
    obj.domain = companyDataContainer && companyDataContainer.domain;
    dispatch(setCompanyDataSlice(obj));
    navigate("/cap/assessments-history");
  };

  // Handles navigation to edit company component
  const handleEditCompany = (companyId) => {
    navigate(`/company-edit/${companyId}`);
  };

  // Opens the confirmation dialog if user clicks on delete
  const handleDeleteClick = (name, location, id) => {
    setCompanyToDelete({ name, location, id });
    setDeleteDialogOpen(true);
  };

  // Deletes the company when user confirms deletion
  const handleDeleteConfirm = async (company_id) => {
    try {
      let deleteStatus = await deleteCompanyService(company_id);
      setDeleteDialogOpen(false);
      if (deleteStatus) {
        openNotification('Company deleted successfully', 'success');
        window.history.back();
      } else {
        openNotification('Error while deleting the company', 'error');
      }
    } catch (error) {
      openNotification('Error while deleting the company', 'error');
    }
  };

  // Method fetches the saved VAPT dimensions based on company id
  const fetchSaveDimensions = async () => {
    try{
      let params = { company: companyId };
      let response = await fetchCompanyVAPTDimensionsService({ params });
      setSelectedDimensions(response);
      setLoading(false);
    }
    catch(error){
    }
    finally{
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSaveDimensions()
    dispatch(fetchCloudTypesService())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect hook Fetches the company profile based on company id
  useEffect(() => {
    dispatch(setErrorsSlice([]));
    if (companyId !== null)
      dispatch(fetchSingleCompany({ companyId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // useEffect hook fetches the Capcompanies service if CAP is enabled for the company and for superadmin role
  useEffect(() => {
    const params = { company: companyId };
    if (role === 'Super Admin' && companyId !== null && companyDataContainer?.applications?.some(app => app.label === "CAP")) {
      dispatch(fetchCapCompaniesService({ params }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div className="position-relative">
  {loading && (
    <div className="card-loader-overlay">
      <CircularProgress size={60} />
    </div>
  )}
    <Card className="main-cont">
      <div className="row header-cont p-2">
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 partner-cont">
          <img src="../assets/partners.svg" className="card-icon" alt="logo" />
          <span className="icon-color text-h5 text-bold font">Partners <PlayArrowIcon /> View Partner</span>
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col lg-3 cont">
          <Button variant="contained" className="back font" onClick={() => window.history.back()}>Back</Button>
        </div>
      </div>
      <Divider className='divider' />
      <CardContent>
        <div className="profile-container justify-center">
          <div className="col-md-12 col-lg-6 col-sm-12 col-xs-12 assessments-tab justify-center">
            <div className="flex">
              <BusinessIcon className="delete-company-dialog-icon" />
              <div className="pt-3 pb-3 " style={{ overflow: 'hidden' }}>
                <h5>{companyDataContainer && companyDataContainer.name}</h5>
                <h5>{companyDataContainer && companyDataContainer.location?.name}</h5>
              </div>
            </div>
            {companyDataContainer && (
              <div id="companyInfo" className="text-body-1 text-grey-7">
                <div className='row pt-2'>
                  <div className="col-4 fw-bold">Email</div>
                  <div className="col-8">{companyDataContainer.email}</div>
                </div>
                <div className='row pt-2'>
                  <div className="col-4 fw-bold">Phone</div>
                  <div className="col-8">+{companyDataContainer.mobile?.code} {companyDataContainer.mobile?.digits}</div>
                </div>
                <div className='row pt-2'>
                  <div className="col-4 fw-bold">Domain URL</div>
                  <div className="col-8">{companyDataContainer.domain}</div>
                </div>
                <div className='row pt-2'>
                  <div className="col-4 fw-bold">Subscription plan</div>
                  <div className="col-8"> {companyDataContainer.subscriptionPlan?.label}</div>
                </div>
                <div className='row pt-2'>
                  <div className="col-4 fw-bold">Industry</div>
                  <div className="col-8">{companyDataContainer.industry?.label}</div>
                </div>
                <div className='row pt-2'>
                  <div className="col-4 fw-bold">Applications</div>
                  <div className="col-8">
                    {companyDataContainer.applications?.map((app, index) => (
                      <span key={app.id}>
                        {app.label}
                        {index < companyDataContainer.applications.length - 1 && ','}
                      </span>
                    ))}
                  </div>
                </div>
                {(companyDataContainer?.applications?.filter((app, index) => app.label === 'VAPT')?.length > 0 && (
                  <div className='row pt-2'>
                    <div className="col-4 fw-bold">Categories</div>
                    <div className="col-8">
                      {
                        ((companyDataContainer?.applications?.filter((app, index) => app.label === 'VAPT')) &&
                          ((companyDataContainer?.applications?.filter((app) => app.label === 'VAPT')?.map((data, index) => data.label)?.length) > 0)) && (
                          selectedDimensions?.map((dim, index) => (
                            <span key={dim?.id}>
                              {dim?.label}
                              {index < selectedDimensions?.length - 1 && ','}
                            </span>
                          ))
                        )
                      }
                    </div>
                  </div>
                ))}
                {companyDataContainer?.webapplications?.length > 0 && (
                  <div className='row pt-2'>
                    <div className="col-4 fw-bold">Web Applications</div>
                    <div className="col-8">
                      {companyDataContainer?.webapplications?.map((app, index) => (
                        <span key={index}>
                          {app}
                          {index < companyDataContainer?.webapplications?.length - 1 && ','}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {companyDataContainer?.network_locations?.length > 0 && (
                  <div className='row pt-2'>
                    <div className="col-4 fw-bold">Network Locations</div>
                    <div className="col-8">
                      {companyDataContainer?.network_locations?.map((app, index) => (
                        <span key={index}>
                          {app}
                          {index < companyDataContainer?.network_locations?.length - 1 && ','}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {companyDataContainer?.mobiletypes?.length > 0 && (
                  <div className='row pt-2'>
                    <div className="col-4 fw-bold">Mobile Types</div>
                    <div className="col-8">
                      {companyDataContainer?.mobiletypes?.map((app, index) => (
                        <span key={index}>
                          {app}
                          {index < companyDataContainer?.mobiletypes?.length - 1 && ','}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {companyDataContainer?.cloudtypes?.length > 0 && (
                  <div className='row pt-2'>
                    <div className="col-4 fw-bold">Cloud Services</div>
                    <div className="col-8">
                      {companyDataContainer?.cloudtypes
                        ?.map((id) => cloudTypesContainer.find((dim) => dim.id === id))
                        .filter((dim) => dim !== undefined)
                        .map((dim, index) => (
                          <span key={dim.id}>
                            {dim.label}
                            {index < companyDataContainer.cloudtypes.length - 1 && ','}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {(user &&
              user.roles &&
              user.roles.some((role) =>
                role.permissions.includes('UPDATE_SELF_COMPANY')
              )
            ) && (
                <Button Variant="contained" onClick={() => handleEditCompany(companyDataContainer.id)} className="col-lg-5 col-md-5 col-sm-12 col-xs-12 edit-btn mt-2" > <EditIcon /> Edit company</Button>
              )}
            {(user &&
              user.roles &&
              user.roles.some((role) =>
                role.permissions.includes('DELETE_ANY_COMPANIES')
              )
            ) && (
                <Button Variant="contained" className="col-lg-5 col-md-5 col-sm-12 col-xs-12 delete-btn mt-2" onClick={() => handleDeleteClick(companyDataContainer.name, companyDataContainer.location ? companyDataContainer.location.name : '', companyDataContainer.id)} > <DeleteIcon /> Delete company</Button>
              )}
          </div>

          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            aria-labelledby="delete-dialog-title"
          >
            <DialogTitle id="delete-dialog-title">
              Are you sure you want to delete the company?
              <IconButton edge="end" color="inherit" onClick={() => setDeleteDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
              <Divider className="divider" />
            </DialogTitle>
            <DialogContent>
              <div className="flex justify-center">
                <BusinessIcon className='delete-company-dialog-icon' />
                <div className='m-1'>
                  <h4>{companyToDelete.name}</h4>
                  <h4>{companyToDelete.location}</h4>
                </div>
              </div>
            </DialogContent>
            <DialogActions className="m-3">
              <Button onClick={() => setDeleteDialogOpen(false)} variant="contained" className="btn-card">
                Cancel
              </Button>
              <Button onClick={() => handleDeleteConfirm(companyToDelete.id)} variant="contained" className="btn-card danger">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {companyDataContainer?.applications && companyDataContainer.applications.some(app => app.label === 'CAP') && (
            <Divider orientation="vertical" className="divider" flexItem />
          )}
          {companyDataContainer?.applications && companyDataContainer.applications.some(app => app.label === 'CAP') && (
            <div className="col-md-12 col-lg-6 col-sm-12 col-xs-12 assessments-tab">
              {companyDataContainer?.applications && companyDataContainer.applications.some(app => app.label === 'CAP') && (
                <h4>Assessment score overview</h4>
              )}
              {capCompaniesContainer?.data && capCompaniesContainer?.data.length > 0 && (
                <div id="assessment">
                  {capCompaniesContainer.data.map((capCompany) => {
                    if (capCompany.id === companyId) {
                      return (
                        <div key={capCompany.id}>
                          {capCompany.assessments?.map((assessment, index) => (
                            <div key={assessment.standard} className="assessment-item">
                              <div>
                                {index + 1}.{assessment.standard}:
                              </div>
                              <div>
                                <b>{assessment.score.toFixed(2)}/100</b>&nbsp;&nbsp;&nbsp; {capCompany.lastassessment.substring(0, 10)}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    } else if (capCompany.customers && capCompany.customers.some(customer => customer.id === companyId)) {
                      const customer = capCompany.customers.find(customer => customer.id === companyId);
                      return (
                        <div key={customer.id} id="assessments" >
                          {customer.assessments?.map((assessment, index) => (
                            <div key={assessment.standard} className="assessment-item">
                              <div>
                                {index + 1}.{assessment.standard}
                              </div>
                              <div>
                                <b>{assessment.score.toFixed(2)}/100</b>&nbsp;&nbsp;&nbsp;{customer.lastassessment.substring(0, 10)}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
              {companyDataContainer?.applications && companyDataContainer.applications.some(app => app.label === 'CAP') && (
                <Button Variant="contained" className="col-lg-6 col-md-6 col-sm-12 col-xs-12 view-assessments" onClick={viewAssessments}> <SearchIcon /> View assessments</Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    </div>
  );
};

export default CompanyProfile;
